import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import routes from '@/routeHelpers'
import { useRouteParams } from '@/hooks/useRoutes'
import { cn } from '@/lib/utils'
import type { AnalyticsFilters as Filters, AnalyticsRangeOption, AnalyticsInboxOption } from '@/types/analytics'

interface AnalyticsFiltersProps {
  filters: Filters
  ranges: AnalyticsRangeOption[]
  inboxes: AnalyticsInboxOption[]
}

export function AnalyticsFilters({ filters, ranges, inboxes }: AnalyticsFiltersProps) {
  const { withAccount } = useRouteParams()

  const update = (partial: Partial<Pick<Filters, 'range' | 'inbox_id'>>) => {
    const next = { range: filters.range, inbox_id: filters.inbox_id, ...partial }
    router.visit(routes.analytics.show.path(withAccount()), {
      data: { range: next.range, inbox_id: next.inbox_id ?? '', section: filters.section },
      preserveScroll: true,
      preserveState: true,
      only: [
        'filters',
        'kpis',
        'conversation_volume',
        'first_response_time',
        'heatmap',
        'channel_mix',
        'top_topics',
        'top_countries',
        'response_time_by_country',
        'agent_performance',
        'ai_suggestions',
        'quick_wins',
      ],
    })
  }

  return (
    <div className="flex flex-wrap items-center gap-3 pb-4 border-b">
      <div className="flex items-center gap-1 rounded-md border bg-muted/30 p-0.5">
        <button
          type="button"
          onClick={() => update({ inbox_id: null })}
          className={cn(
            'px-3 py-1 text-xs rounded cursor-pointer transition-colors',
            !filters.inbox_id ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          All inboxes
        </button>
        {inboxes.map((inbox) => (
          <button
            key={inbox.id}
            type="button"
            onClick={() => update({ inbox_id: inbox.id })}
            className={cn(
              'px-3 py-1 text-xs rounded cursor-pointer transition-colors',
              filters.inbox_id === inbox.id ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {inbox.name}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1 rounded-md border bg-muted/30 p-0.5">
        {ranges.map((range) => (
          <button
            key={range.key}
            type="button"
            onClick={() => update({ range: range.key })}
            className={cn(
              'px-3 py-1 text-xs rounded cursor-pointer transition-colors',
              filters.range === range.key ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {range.key}
          </button>
        ))}
      </div>

      <div className="ml-auto">
        <Button variant="outline" size="sm" disabled title="Export coming soon">
          Export
        </Button>
      </div>
    </div>
  )
}
